import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setStatus('error');
      setErrorMessage('Please fill in all fields');
      return;
    }

    if (formData.message.length > 500) {
      setStatus('error');
      setErrorMessage('Message must be 500 characters or less');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d5nshpof4392hm13o7kg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          name: formData.name,
          email: formData.email,
          message: formData.message,
        }).toString(),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage('Failed to send message. Please try again.');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('An error occurred. Please try again.');
    }
  };

  const handleWhatsApp = () => {
    window.open('https://wa.me/message/YOURIDHERE', '_blank');
  };

  return (
    <section id="contact" className="py-32 bg-[#231D17] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#B78A44] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-5xl md:text-6xl font-bold text-[#E5C478] mb-8 text-center tracking-wide">
            Contact The GodPadel™
          </h2>

          <p className="text-lg text-[#F0E1C2]/80 text-center mb-16 max-w-2xl mx-auto">
            Whether you're an investor, partner, or padel enthusiast, we'd like to hear from you.
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <form
                id="godpadel-contact-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div>
                  <label htmlFor="name" className="block text-[#F0E1C2] mb-2 font-medium">
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 bg-[#2A2318] border border-[#B78A44]/30 rounded-md text-[#F0E1C2] focus:outline-none focus:border-[#B78A44] transition-colors text-base"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-[#F0E1C2] mb-2 font-medium">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#2A2318] border border-[#B78A44]/30 rounded-md text-[#F0E1C2] focus:outline-none focus:border-[#B78A44] transition-colors text-base"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-[#F0E1C2] mb-2 font-medium">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-[#2A2318] border border-[#B78A44]/30 rounded-md text-[#F0E1C2] focus:outline-none focus:border-[#B78A44] transition-colors resize-none text-base"
                    required
                  ></textarea>
                  <p className="text-sm text-[#F0E1C2]/60 mt-2">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full bg-[#B78A44] text-[#231D17] px-8 py-4 rounded-md font-semibold hover:bg-[#E5C478] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {status === 'submitting' ? 'Sending...' : 'Send Message'}
                </button>

                {status === 'success' && (
                  <div className="p-4 bg-[#B78A44]/20 border border-[#B78A44] rounded-md">
                    <p className="text-[#E5C478] text-center">
                      Thank you for your message. We'll be in touch soon.
                    </p>
                  </div>
                )}

                {status === 'error' && (
                  <div className="p-4 bg-red-900/20 border border-red-800 rounded-md">
                    <p className="text-red-400 text-center">{errorMessage}</p>
                  </div>
                )}
              </form>
            </div>

            <div className="flex flex-col justify-center space-y-8">
              <div className="bg-gradient-to-br from-[#2A2318] to-[#231D17] p-8 rounded-lg border border-[#B78A44]/20">
                <div className="flex items-center justify-center w-16 h-16 bg-[#B78A44] rounded-full mb-6 mx-auto">
                  <i className="ri-whatsapp-line text-3xl text-[#231D17]"></i>
                </div>
                <h3 className="font-serif text-2xl font-bold text-[#E5C478] mb-4 text-center">
                  Instant Messaging
                </h3>
                <p className="text-[#F0E1C2]/80 text-center mb-6 leading-relaxed">
                  Prefer WhatsApp? Connect with us instantly for quick enquiries and updates.
                </p>
                <button
                  onClick={handleWhatsApp}
                  className="w-full bg-[#25D366] text-white px-8 py-3 rounded-md font-semibold hover:bg-[#20BA5A] transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  Message on WhatsApp
                </button>
              </div>

              <div className="text-center space-y-4">
                <p className="text-[#F0E1C2]/60 text-sm">
                  Or email us directly at
                </p>
                <a
                  href="mailto:contact@thegodpadel.com"
                  className="text-[#E5C478] hover:text-[#B78A44] transition-colors text-lg font-medium cursor-pointer"
                >
                  contact@thegodpadel.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
